import { useCallback, useRef, useState } from 'react';
import { bytesToHex, hexToBytes, type Hex } from 'viem';

import type { ExternalSigningDeviceMetadata } from '@infinex-private/connect-types';
import type { SigningRequestMetadata } from '@infinex/connect-service-contract/client';
import type { HardwareWalletManager } from '@infinex/hardware-wallets/web';

export type HardwareWalletSigningState =
  | 'idle'
  | 'connecting'
  | 'awaiting_confirmation'
  | 'signing'
  | 'broadcasting'
  | 'success'
  | 'error';

export function useHardwareWalletSigning() {
  const [state, setState] = useState<HardwareWalletSigningState>('idle');
  const [error, setError] = useState<Error | null>(null);
  const managerRef = useRef<HardwareWalletManager | null>(null);

  /**
   * Wraps a signing operation with connection, state management, and cleanup.
   */
  const withSigningSession = useCallback(
    async <T>(
      device: ExternalSigningDeviceMetadata,
      signingAddress: string,
      runtime: 'evm' | 'svm',
      operation: (manager: HardwareWalletManager) => Promise<T>,
      errorMessage = 'Signing failed'
    ): Promise<{ result?: T; error?: Error }> => {
      try {
        setState('connecting');
        setError(null);

        if (!device.deviceType || !device.derivationPath) {
          throw new Error('Hardware wallet configuration incomplete');
        }

        const { HardwareWalletManager } =
          await import('@infinex/hardware-wallets/web');

        const manager = new HardwareWalletManager(device.deviceType);
        managerRef.current = manager;

        await manager.connect({
          runtime,
          account: {
            address: signingAddress,
            derivationPath: device.derivationPath,
            extendedPublicKey: device.extendedPublicKey ?? undefined,
          },
        });

        setState('awaiting_confirmation');

        const result = await operation(manager);

        setState('success');
        return { result };
      } catch (err) {
        const error = err instanceof Error ? err : new Error(errorMessage);
        setError(error);
        setState('error');
        return { error };
      } finally {
        await managerRef.current?.disconnect();
        managerRef.current = null;
      }
    },
    []
  );

  const reset = useCallback(() => {
    setState('idle');
    setError(null);
  }, []);

  /**
   * Signs a request using the hardware wallet based on the request metadata.
   */
  const signWithHardwareWallet = useCallback(
    async (metadata: SigningRequestMetadata): Promise<string> => {
      const {
        externalSigningDevice,
        signingAddress,
        hardwareWalletTxData,
        input,
      } = metadata;

      if (!externalSigningDevice?.isExternalSigningDevice) {
        throw new Error('This wallet is not a hardware wallet');
      }

      const inputType = input.type as string;

      if (inputType === 'evm:message') {
        const evmInput = input as { message: string | { raw: unknown } };
        const message = evmInput.message;
        const messageStr =
          typeof message === 'object' && message.raw
            ? String(message.raw)
            : String(message);

        const { result, error } = await withSigningSession(
          externalSigningDevice,
          signingAddress,
          'evm',
          (manager) =>
            manager.signEvmMessage({
              address: signingAddress,
              message: messageStr,
            })
        );

        if (error) throw error;
        if (!result) throw new Error('No signature returned');
        return result;
      }

      if (inputType === 'evm:typedDataV4') {
        const evmInput = input as { typedData: Record<string, unknown> };

        const { result, error } = await withSigningSession(
          externalSigningDevice,
          signingAddress,
          'evm',
          (manager) =>
            manager.signEvmTypedData({
              address: signingAddress,
              data: evmInput.typedData,
              version: 'V4',
            })
        );

        if (error) throw error;
        if (!result) throw new Error('No signature returned');
        return result;
      }

      if (inputType === 'evm:transaction') {
        if (!hardwareWalletTxData) {
          throw new Error('Transaction data not available. Please try again.');
        }

        const { result, error } = await withSigningSession(
          externalSigningDevice,
          signingAddress,
          'evm',
          async (manager) => {
            const { createFeeMarket1559Tx } = await import('@ethereumjs/tx');
            const { createCustomCommon, Mainnet } =
              await import('@ethereumjs/common');

            // Use Mainnet as base template but override chainId for L2 support
            const common = createCustomCommon(
              { chainId: hardwareWalletTxData.chainId },
              Mainnet
            );

            const tx = createFeeMarket1559Tx(
              {
                nonce: BigInt(hardwareWalletTxData.nonce),
                maxFeePerGas: BigInt(hardwareWalletTxData.maxFeePerGas),
                maxPriorityFeePerGas: BigInt(
                  hardwareWalletTxData.maxPriorityFeePerGas
                ),
                gasLimit: BigInt(hardwareWalletTxData.gasLimit),
                to: hardwareWalletTxData.to as Hex,
                value: BigInt(hardwareWalletTxData.value),
                data: (hardwareWalletTxData.data as Hex) || '0x',
                chainId: BigInt(hardwareWalletTxData.chainId),
              },
              // @ts-expect-error - Type mismatch between @ethereumjs/common and @ethereumjs/tx
              { common }
            );

            const signedTx = (await manager.signEvmTransaction({
              address: signingAddress,
              tx,
            })) as typeof tx;

            setState('signing');

            return bytesToHex(signedTx.serialize());
          },
          'Transaction failed'
        );

        if (error) throw error;
        if (!result) throw new Error('No signed transaction returned');
        return result;
      }

      if (inputType === 'svm:signMessage') {
        const svmInput = input as { message: string };
        if (!svmInput.message) {
          throw new Error('Message not available. Please try again.');
        }

        const { result, error } = await withSigningSession(
          externalSigningDevice,
          signingAddress,
          'svm',
          async (manager) => {
            const messageBytes = hexToBytes(svmInput.message as Hex);
            const signature = await manager.signSvmMessage({
              address: signingAddress,
              message: messageBytes,
            });

            return bytesToHex(signature);
          },
          'Solana message signing failed'
        );

        if (error) throw error;
        if (!result) throw new Error('No signature returned');
        return result;
      }

      if (inputType === 'svm:transaction') {
        // SVM transaction bytes are passed as base64 in the signing input
        const svmInput = input as { transaction: string };
        if (!svmInput.transaction) {
          throw new Error(
            'Solana transaction data not available. Please try again.'
          );
        }

        const { result, error } = await withSigningSession(
          externalSigningDevice,
          signingAddress,
          'svm',
          async (manager) => {
            const serializedTx = Uint8Array.from(
              atob(svmInput.transaction),
              (c) => c.charCodeAt(0)
            );

            const signature = await manager.signSvmTransaction({
              address: signingAddress,
              transaction: serializedTx,
            });

            // Return base64-encoded signature
            return btoa(String.fromCharCode(...signature));
          },
          'Solana transaction signing failed'
        );

        if (error) throw error;
        if (!result) throw new Error('No signed transaction returned');
        return result;
      }

      throw new Error(`Unsupported signing type: ${inputType}`);
    },
    [withSigningSession]
  );

  return {
    state,
    error,
    signWithHardwareWallet,
    reset,
  };
}
